// TEN17-F5 (docs/build-briefs/TEN17-F5-brand-colors-fonts.md; DEC-429): the free fonts a franchisor can pick for its
// brand, in one PURE list (no I/O, no Node-only import) shared by the server, the franchisor page and the app.
//
// Every face is SIL Open Font License 1.1 or Apache 2.0 and is self-hosted (design/fonts/), never loaded from a
// CDN. The four faces TEN17 already shipped keep their files; the rest come from the pinned @fontsource packages
// through scripts/sync-brand-fonts.ts, which writes design/fonts/brand/manifest.json (each file's SHA-256 and
// license) and one stylesheet per face under design/themes/brand-fonts/. test/ten17-f5-font-catalog.test.ts holds
// this list to those files.
//
// A face's stylesheet is linked only while a brand that uses it is applied, and a browser fetches a face's file
// only when text uses it, so nothing here puts a font in a JavaScript bundle.

export type BrandFontCategory = "sans" | "serif" | "display" | "system";
export type BrandFontLicense = "OFL-1.1" | "Apache-2.0";

export interface CatalogFont {
  readonly id: string;
  readonly label: string;
  /** The CSS family name the face's @font-face declares. */
  readonly family: string;
  readonly category: BrandFontCategory;
  /** A display face sets headings and totals only; body text keeps the Instrument Sans body stack. */
  readonly display: boolean;
  readonly license: BrandFontLicense;
  /** The @fontsource package this face is copied from, or null for the four faces shipped before TEN17-F5. */
  readonly fontsource: string | null;
  /** Weights shipped (latin subset). */
  readonly weights: readonly number[];
  /** The generic family the stack ends with when the face is not available. */
  readonly fallback: "sans" | "serif";
}

const sans = (id: string, label: string, fontsource: string, weights: readonly number[] = [400, 700]): CatalogFont => ({
  id,
  label,
  family: label,
  category: "sans",
  display: false,
  license: "OFL-1.1",
  fontsource,
  weights,
  fallback: "sans",
});
const serif = (
  id: string,
  label: string,
  fontsource: string,
  weights: readonly number[] = [400, 700],
  license: BrandFontLicense = "OFL-1.1",
): CatalogFont => ({ id, label, family: label, category: "serif", display: false, license, fontsource, weights, fallback: "serif" });
const display = (
  id: string,
  label: string,
  fontsource: string,
  weights: readonly number[] = [400],
  fallback: "sans" | "serif" = "sans",
): CatalogFont => ({
  id,
  label,
  family: label,
  category: "display",
  display: true,
  license: "OFL-1.1",
  fontsource,
  weights,
  fallback,
});

/** The catalog, in the order the dropdown lists it inside each group. The system stack is not a file and is
 * listed separately (`SYSTEM_FONT_ID`). */
export const CATALOG_FONTS: readonly CatalogFont[] = [
  // Shipped before TEN17-F5 (design/fonts/, brand-fonts.css).
  { id: "bricolage-grotesque", label: "Bricolage Grotesque", family: "Bricolage Grotesque", category: "display", display: true, license: "OFL-1.1", fontsource: null, weights: [200, 800], fallback: "sans" },
  { id: "instrument-sans", label: "Instrument Sans", family: "Instrument Sans", category: "sans", display: false, license: "OFL-1.1", fontsource: null, weights: [400, 700], fallback: "sans" },
  { id: "montserrat", label: "Montserrat", family: "Montserrat", category: "sans", display: true, license: "OFL-1.1", fontsource: null, weights: [400, 800], fallback: "sans" },
  { id: "bitter", label: "Bitter (italic)", family: "Bitter", category: "serif", display: true, license: "OFL-1.1", fontsource: null, weights: [400, 700], fallback: "serif" },
  // Sans serif.
  sans("inter", "Inter", "inter"),
  sans("roboto", "Roboto", "roboto"),
  sans("open-sans", "Open Sans", "open-sans"),
  sans("lato", "Lato", "lato"),
  sans("poppins", "Poppins", "poppins"),
  sans("nunito", "Nunito", "nunito"),
  sans("work-sans", "Work Sans", "work-sans"),
  sans("dm-sans", "DM Sans", "dm-sans"),
  sans("manrope", "Manrope", "manrope"),
  sans("raleway", "Raleway", "raleway"),
  sans("rubik", "Rubik", "rubik"),
  sans("karla", "Karla", "karla"),
  sans("source-sans-3", "Source Sans 3", "source-sans-3"),
  sans("fira-sans", "Fira Sans", "fira-sans"),
  sans("public-sans", "Public Sans", "public-sans"),
  sans("barlow", "Barlow", "barlow"),
  sans("outfit", "Outfit", "outfit"),
  sans("plus-jakarta-sans", "Plus Jakarta Sans", "plus-jakarta-sans"),
  sans("josefin-sans", "Josefin Sans", "josefin-sans"),
  // Serif.
  serif("playfair-display", "Playfair Display", "playfair-display"),
  serif("merriweather", "Merriweather", "merriweather"),
  serif("lora", "Lora", "lora"),
  serif("cormorant-garamond", "Cormorant Garamond", "cormorant-garamond"),
  serif("libre-baskerville", "Libre Baskerville", "libre-baskerville"),
  serif("eb-garamond", "EB Garamond", "eb-garamond"),
  serif("crimson-pro", "Crimson Pro", "crimson-pro"),
  serif("roboto-slab", "Roboto Slab", "roboto-slab", [400, 700], "Apache-2.0"),
  // Display.
  display("oswald", "Oswald", "oswald", [400, 700]),
  display("dm-serif-display", "DM Serif Display", "dm-serif-display", [400], "serif"),
  display("cinzel", "Cinzel", "cinzel", [400, 700], "serif"),
  display("abril-fatface", "Abril Fatface", "abril-fatface", [400], "serif"),
  display("bebas-neue", "Bebas Neue", "bebas-neue"),
  display("anton", "Anton", "anton"),
];

export const SYSTEM_FONT_ID = "system";
export const SYSTEM_FONT_LABEL = "System font";

/** The font a brand falls back to when its uploaded font is removed. */
export const DEFAULT_BRAND_FONT_ID = "instrument-sans";

export const CATALOG_FONT_IDS: readonly string[] = [...CATALOG_FONTS.map((font) => font.id), SYSTEM_FONT_ID];

export const BRAND_FONT_GROUPS: ReadonlyArray<{ readonly id: BrandFontCategory | "uploaded"; readonly label: string }> = [
  { id: "sans", label: "Sans serif" },
  { id: "serif", label: "Serif" },
  { id: "display", label: "Display" },
  { id: "system", label: "System" },
  { id: "uploaded", label: "Uploaded fonts" },
];

export function catalogFont(id: string): CatalogFont | undefined {
  return CATALOG_FONTS.find((font) => font.id === id);
}

/** The prefix of an uploaded font's id in a brand's `font` field: `upload:<uuid>`. */
export const UPLOADED_FONT_PREFIX = "upload:";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** The font row id inside an `upload:<uuid>` brand font, or null when the value is not of that exact shape. */
export function uploadedFontIdOf(fontId: string): string | null {
  if (!fontId.startsWith(UPLOADED_FONT_PREFIX)) return null;
  const id = fontId.slice(UPLOADED_FONT_PREFIX.length);
  return UUID.test(id) ? id : null;
}

export function uploadedFontBrandId(fontRowId: string): string {
  return `${UPLOADED_FONT_PREFIX}${fontRowId}`;
}

/** The CSS family name an uploaded font is registered under in the page. A fixed shape built from the row id, never
 * from the uploaded file name, so a name can carry no CSS. */
export function uploadedFontFamily(fontRowId: string): string {
  return `brand-upload-${fontRowId}`;
}
